import { useRef } from "react";

interface DisclosureProps extends React.HTMLAttributes<HTMLDetailsElement> {
	readonly children: [
		React.ReactElement<HTMLElement>,
		...React.ReactNode[]
	];
}
export function Disclosure(props: DisclosureProps) {
	const animation = useRef<Animation | null>(null);
	const isClosing = useRef(false);
	const isExpanding = useRef(false);

	const el = useRef<HTMLDetailsElement>(null);
	const summary = useRef<HTMLElement>(null);
	const content = useRef<HTMLDivElement>(null);

	const summaryContent = props.children[0];
	const contentContent = props.children.slice(1);

	const onClickSummary = (e: React.MouseEvent) => {
		e.preventDefault();
		if (el.current === null || summary.current === null) return;

		el.current.style.overflow = "hidden";

		if (isClosing.current) {
			open();
		} else if (isExpanding.current) {
			shrink();
		} else if (el.current.open) {
			shrink();
		} else {
			open();
		}
	};


	function shrink() {
		if (el.current === null || summary.current === null) return;
		isClosing.current = true;


		const startHeight = `${el.current.offsetHeight}px`;
		const endHeight = `${summary.current.offsetHeight}px`;

		if (animation.current) {
			animation.current.cancel();
		}

		// Start a WAAPI animation
		animation.current = el.current.animate({
			// Set the keyframes from the startHeight to endHeight
			height: [startHeight, endHeight]
		}, {
			duration: 400,
			easing: "ease-out"
		});

		// When the animation is complete, call onAnimationFinish()
		animation.current.onfinish = () => onAnimationFinish(false);
		// If the animation is cancelled, isClosing variable is set to false
		animation.current.oncancel = () => isClosing.current = false;
	}

	function open() {
		if (el.current === null || summary.current === null) return;
		el.current.style.height = `${el.current.offsetHeight}px`;

		el.current.open = true;
		window.requestAnimationFrame(() => expand());
	}

	function expand() {
		if (el.current === null || summary.current === null || content.current === null) return;

		isExpanding.current = true;
		const startHeight = `${el.current.offsetHeight}px`;
		const endHeight = `${summary.current.offsetHeight + content.current.offsetHeight}px`;

		// If there is already an animation running
		if (animation.current) {
			animation.current.cancel();
		}

		const a = el.current.animate(
			{ height: [startHeight, endHeight] },
			{
				duration: 400,
				easing: "ease-out"
			}
		);

		a.onfinish = () => onAnimationFinish(true);
		a.oncancel = () => isExpanding.current = false;

		animation.current = a;
	}

	function onAnimationFinish(open: boolean) {
		if (el.current === null) return;

		el.current.open = open;
		animation.current = null;
		isClosing.current = false;
		isExpanding.current = false;

		el.current.style.height = "";
		el.current.style.overflow = "";
	}

	// Lo metemos en un div para evitar problemas cuando el padre es un flexbox
	return (
		<div>
			<details ref={el} {...props} open>
				<summary ref={summary} onClick={onClickSummary} >{summaryContent}</summary>
				<div ref={content}>{contentContent}</div>
			</details>
		</div>
	);
}
